import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { OptionsService } from '../../../shared/services/options.service';
import {
  VehicleOption,
  VehicleOptionWriteRequest,
} from '../../../shared/types/option.types';
import { OptionFormComponent } from '../option-form/option-form.component';

@Component({
  selector: 'app-option-edit',
  imports: [
    PageHeaderComponent,
    DataStateComponent,
    OptionFormComponent,
  ],
  templateUrl: './option-edit.component.html',
})
export class OptionEditComponent implements OnInit {
  private readonly service = inject(OptionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly item = signal<VehicleOption | null>(null);
  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.service
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (o) => {
          this.item.set(o);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cet équipement.'),
          );
        },
      });
  }

  onSave(payload: VehicleOptionWriteRequest): void {
    this.submitting.set(true);
    this.serverError.set(null);
    this.service
      .update(this.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          void this.router.navigate(['/options', this.id]);
        },
        error: (err: unknown) => {
          this.submitting.set(false);
          this.serverError.set(
            toErrorMessage(err, 'errors.updateFailed', {
              409: 'Un équipement avec ce slug existe déjà.',
            }),
          );
        },
      });
  }

  onCancel(): void {
    void this.router.navigate(['/options', this.id]);
  }
}
